/**
 * Document queries, and the upload machine behind Brain → Sources.
 *
 * Upload is three calls — presign, PUT to S3, complete — and the middle one is
 * a raw `XMLHttpRequest` rather than `fetch`, because `fetch` still cannot
 * report *upload* progress and the design shows a determinate bar per row.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  MAX_STATUS_GROUP,
  PlaceDocumentInputSchema,
  SetDocumentCategoryInputSchema,
  documentUploadRefusal,
  type FileStatus,
  type ImportStatus,
  type PlaceDocumentInput,
  type ProjectDocument,
  type ProjectImport,
} from "@gb/contracts";
import { api, errorMessage } from "@/api/client";
import { withFolders, type PickedFile } from "@/lib/filed-folders";
import { putWithProgress } from "@/lib/upload";
import { UploadRefused, isRefusal, uploadFailure } from "@/lib/upload-failure";

export const documentKeys = {
  list: (projectId: string) => ["project", projectId, "documents"] as const,
};

/**
 * The list is a prefix of every single import's key, so invalidating the list
 * re-reads whichever import a screen is following as well.
 */
export const importKeys = {
  list: (projectId: string) => ["project", projectId, "imports"] as const,
  detail: (projectId: string, importId: string) =>
    ["project", projectId, "imports", importId] as const,
};

/**
 * How often to re-read the list while a document is still being processed.
 *
 * The card is written *after* the upload completes — the pipeline is queued
 * fire-and-forget, and the row lands with no title, type or category until it
 * finishes. The list used to be invalidated once, immediately after
 * `completeUpload`, and never again, so the checklist showed what the row said
 * a second after upload and nothing moved it afterwards. That reads to the user
 * as a checklist that ignores the file they just gave it.
 */
const PROCESSING_POLL_MS = 3_000;

/** Whether the pipeline is still reading any of these documents. */
export function anyProcessing(documents: readonly ProjectDocument[]): boolean {
  return documents.some(
    (document) =>
      document.processingStatus === "queued" ||
      document.processingStatus === "running",
  );
}

export function useDocuments(projectId: string | undefined) {
  return useQuery({
    queryKey: documentKeys.list(projectId ?? ""),
    enabled: Boolean(projectId),
    queryFn: async (): Promise<ProjectDocument[]> => {
      const res = await api.projects.listDocuments({
        params: { id: projectId! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.documents;
    },
    // Only while something is actually in flight — settled lists do not poll.
    refetchInterval: (query) =>
      anyProcessing(query.state.data ?? []) ? PROCESSING_POLL_MS : false,
  });
}

/**
 * Re-read everything that shows where a file sits: the documents and the
 * Brain. Called once an import has been organised, and after a person moves a
 * file. The Brain is its own key — see the note in `connectors-api.ts`.
 */
function invalidatePlacement(queryClient: QueryClient, projectId: string): void {
  void queryClient.invalidateQueries({ queryKey: documentKeys.list(projectId) });
  void queryClient.invalidateQueries({ queryKey: ["project", projectId, "brain"] });
  // Every move here writes a line to the change log, which the review screen
  // lists (`review-api.ts`). Spelled out rather than imported from there: that
  // file imports this one.
  void queryClient.invalidateQueries({ queryKey: ["project", projectId, "changes"] });
}

/**
 * A person moves a file to one of the tenant's categories, an empty one
 * included (`document-ai/0032`, 2026-10-03). It sets the main category only:
 * the card's other categories stay, and no card written later moves it back.
 *
 * Checked against the contract's own schema before it is sent. Whether the
 * key is in the tenant's list only the server knows, and its refusal is one
 * sentence, shown as it is. Everything that shows where a file sits is
 * re-read afterwards, the change log included: a move is a line in it.
 */
export function useSetDocumentCategory(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { documentId: string; category: string }): Promise<ProjectDocument> => {
      const parsed = SetDocumentCategoryInputSchema.safeParse({ category: vars.category });
      if (!parsed.success) throw new Error("Choose a category.");
      const res = await api.projects.setDocumentCategory({
        params: { id: projectId, docId: vars.documentId },
        body: parsed.data,
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.document;
    },
    onSuccess: () => invalidatePlacement(queryClient, projectId),
  });
}

/**
 * What an Undo says when the server refuses it with a 409 and gives no reason.
 *
 * The server's own message says which refusal it is: the files have changed
 * since, the change was already undone, or it cannot be undone at all
 * (`plans/project-files-build.md`, 3d). This is only for a body with none.
 */
export const UNDO_REFUSED_MESSAGE = "This file has changed since. Place it by hand instead.";

/** The server's message on an error body, or null when it sent none. */
function serverMessage(body: unknown): string | null {
  if (!body || typeof body !== "object" || !("message" in body)) return null;
  const { message } = body;
  const text = Array.isArray(message) ? message.join(", ") : message;
  return typeof text === "string" && text.trim() ? text : null;
}

/**
 * A person places a file among its versions (`document-ai/0016`). Answering
 * the model's question is the same call: "Yes" sends the question's relation,
 * "No" sends `on-its-own`. No later import moves the file afterwards.
 *
 * Checked against the contract's own schema before it is sent, like a move.
 * A 409 is a placement the server's checks refuse, such as a loop. Its message
 * says which, so it is shown as it is. The lists are re-read on a 409 as well,
 * because a refusal usually means the screen is showing an old state.
 */
export function usePlaceDocument(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      documentId: string;
      placement: PlaceDocumentInput;
    }): Promise<ProjectDocument> => {
      const parsed = PlaceDocumentInputSchema.safeParse(vars.placement);
      if (!parsed.success) throw new Error("Choose the other file.");
      const res = await api.projects.placeDocument({
        params: { id: projectId, docId: vars.documentId },
        body: parsed.data,
      });
      if (res.status === 409) invalidatePlacement(queryClient, projectId);
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.document;
    },
    onSuccess: () => invalidatePlacement(queryClient, projectId),
  });
}

/**
 * Undo a placement: the change named by a file's `family.undoChangeId`.
 *
 * The server writes a new entry that reverses it, and the file then counts as
 * placed by a person. A 409 is shown in the server's words, which say why, or
 * as `UNDO_REFUSED_MESSAGE` when it gave none. The lists are re-read too, so
 * the screen stops offering an Undo that no longer applies.
 */
export function useUndoChange(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changeId: string): Promise<string> => {
      const res = await api.projects.undoChange({
        params: { id: projectId, changeId },
      });
      if (res.status === 409) {
        invalidatePlacement(queryClient, projectId);
        throw new Error(serverMessage(res.body) ?? UNDO_REFUSED_MESSAGE);
      }
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.changeId;
    },
    onSuccess: () => invalidatePlacement(queryClient, projectId),
  });
}

/**
 * A person says what a file is (`document-ai/0013`). Keeping a file the check
 * flagged is `this-project`; removing it is `useDeleteDocument` below.
 *
 * The check never re-judges a file a person has ruled on, so this is the last
 * word on it. Everything that shows where a file sits is re-read afterwards,
 * for the same reason a placement re-reads it: a kept file rejoins the Brain's
 * ordinary lists, and the shelf that asks is one shorter.
 *
 * A refusal is shown in the server's own words. It sends a readable one for
 * both cases: a 400 for a status the schema refuses, a 409 for a file whose
 * bytes never landed. The contract declares no 409 on this route, so it
 * arrives through the same non-200 branch rather than being narrowed on.
 */
export function useSetDocumentStatus(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      documentId: string;
      status: FileStatus;
      reason?: string;
    }): Promise<ProjectDocument> => {
      const res = await api.projects.setDocumentStatus({
        params: { id: projectId, docId: vars.documentId },
        body: {
          status: vars.status,
          ...(vars.reason ? { reason: vars.reason } : {}),
        },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.document;
    },
    onSuccess: () => invalidatePlacement(queryClient, projectId),
  });
}

/**
 * A person says what a whole group of files is (2026-09-26): keeps every file
 * in a group, or hides every file the check asked about. Sent in parts of
 * `MAX_STATUS_GROUP`, one after another, so a group of any size goes. A part
 * refused stops the rest, and the error says why; the parts already sent stay
 * done, and the list is re-read either way so it shows what changed.
 */
export function useSetDocumentStatuses(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { documentIds: string[]; status: FileStatus }): Promise<number> => {
      let updated = 0;
      for (let at = 0; at < vars.documentIds.length; at += MAX_STATUS_GROUP) {
        const res = await api.projects.setDocumentStatuses({
          params: { id: projectId },
          body: { documentIds: vars.documentIds.slice(at, at + MAX_STATUS_GROUP), status: vars.status },
        });
        if (res.status !== 200) throw new Error(errorMessage(res));
        updated += res.body.updated;
      }
      return updated;
    },
    onSettled: () => invalidatePlacement(queryClient, projectId),
  });
}

export function useDeleteDocument(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: string) => {
      const res = await api.projects.deleteDocument({
        params: { id: projectId, docId: documentId },
      });
      if (res.status !== 204) throw new Error(errorMessage(res));
    },
    // Everything that shows where a file sits: a deleted file leaves its
    // category's count, hands its place among its versions on, and that hand-on
    // is a line in the change log. Its import's counts and failures move too,
    // which is what takes an unfinished upload off the review (2026-09-25).
    onSuccess: () => {
      invalidatePlacement(queryClient, projectId);
      void queryClient.invalidateQueries({ queryKey: importKeys.list(projectId) });
    },
  });
}

/**
 * Why this file would be refused as a project document, or null.
 *
 * The same function the contract and the API run (`architecture/0031`), on the
 * same values the presign would send. A file refused here never makes a
 * request, and the person reads the sentence at once.
 */
export function documentRefusal(file: File): string | null {
  return (
    documentUploadRefusal({
      filename: file.name,
      mimeType: file.type || "application/octet-stream",
      sizeBytes: file.size,
    })?.reason ?? null
  );
}

/**
 * Upload a phased plan and return the stored document.
 *
 * It arrives in an import of its own, opened for the programme (2026-10-03),
 * like every other upload. Before that it arrived outside any import, so it
 * left no record of its own. The import is organised as any import is, and
 * the review labels it. If the import cannot be opened, the file still goes
 * up, on its own, as any upload does.
 *
 * The document is stored *before* any parsing is attempted — storing the
 * original is the hard requirement; the parse is a bonus.
 *
 * The project id arrives per call rather than at hook time, which step 1
 * requires and step 2 does not care about. On step 1 the project is created in
 * the same handler that uploads, so an id captured when the component rendered
 * is still `undefined` by the time the upload runs — `setState` has not
 * re-rendered anything yet. That produced a presign against
 * `/projects/undefined/...`, and the failure was a 404 from a route that looked
 * unrelated to the upload.
 */
export function useUploadPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      file,
    }: {
      projectId: string;
      file: File;
    }) => {
      const refused = documentRefusal(file);
      if (refused) throw new UploadRefused(refused);

      const importId = await openImport(projectId, { programme: true });
      try {
        const presign = await api.projects.uploadUrl({
          params: { id: projectId },
          body: {
            filename: file.name,
            mimeType: file.type || "application/octet-stream",
            sizeBytes: file.size,
            ...(importId ? { importId } : {}),
          },
        });
        if (presign.status !== 201) throw new Error(errorMessage(presign));

        await putWithProgress(presign.body.uploadUrl, file, () => undefined);

        const complete = await api.projects.completeUpload({
          params: { id: projectId, docId: presign.body.documentId },
          body: {},
        });
        if (complete.status !== 200) throw new Error(errorMessage(complete));
        return complete.body.document;
      } finally {
        // Closed whether the upload worked or not. An import of one file is
        // complete once that file has settled, and a failed upload is then
        // listed on it as one that did not finish (`document-ai/0019` §8).
        if (importId) await closeImport(projectId, importId);
      }
    },
    onSettled: (_document, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: documentKeys.list(variables.projectId),
      });
      // The import it opened, which the lists then follow to its end.
      void queryClient.invalidateQueries({ queryKey: importKeys.list(variables.projectId) });
    },
  });
}

/**
 * Where the download route lives — a 302 to a signed URL, `attachment`.
 *
 * A **path**, not a URL, and fetched rather than linked: the route is behind
 * `AuthGuard`, and a top-level navigation sends no bearer token. See
 * `downloadAuthedFile`.
 */
export { documentDownloadPath as downloadPath } from "@gb/contracts";

// ------------------------------------------------------------------
// Imports
// ------------------------------------------------------------------

/**
 * How often an import is re-read while its files are still arriving.
 *
 * A Drive pick is accepted at once and fetched by the worker afterwards
 * (`architecture/0028`), so its files land over the following seconds. Unlike a
 * local upload, nothing on screen stands in for them while they do, and nothing
 * else would tell the lists that they have landed.
 */
const IMPORT_POLL_MS = 3_000;

/** Files are still arriving, or still having their quick pass. */
const ARRIVING: ReadonlySet<ImportStatus> = new Set(["open", "reading"]);

/**
 * The server is working on it, so its status moves without anyone asking.
 * `open` is not here: an open import waits for its client to close it.
 */
const IN_HAND: ReadonlySet<ImportStatus> = new Set(["reading", "organising"]);

/** Organised, or never going to be: its files are as organised as they will be. */
const SETTLED: ReadonlySet<ImportStatus> = new Set(["review", "done", "failed"]);

export function importIsArriving(current: ProjectImport): boolean {
  return ARRIVING.has(current.status);
}

/**
 * One import, polled until it has been organised (`document-ai/0019` §2).
 *
 * The poll carries on through `organising`, because that is when the files are
 * organised, and stops at `review`, `done` or `failed`. `until` stops it
 * earlier, but only while files are still arriving: an import a screen keeps
 * open stays `open` for as long as the screen is up, so its status alone would
 * poll for ever, and only the caller knows when what it was waiting for has
 * come.
 *
 * Whenever the counts move, the documents list and the Brain are re-read. A
 * count going up is a file that has landed or been carded, and neither would
 * know it otherwise: a file's category comes with its card
 * (`document-ai/0032`). Once the import settles, the same two are re-read so
 * what the organising did appears.
 */
export function useImport(
  projectId: string | undefined,
  importId: string | undefined,
  options: { until?: (current: ProjectImport) => boolean } = {},
) {
  const queryClient = useQueryClient();
  const { until } = options;
  const query = useQuery({
    queryKey: importKeys.detail(projectId ?? "", importId ?? ""),
    enabled: Boolean(projectId && importId),
    queryFn: async (): Promise<ProjectImport> => {
      const res = await api.projects.getImport({
        params: { id: projectId!, importId: importId! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.import;
    },
    refetchInterval: (query) => {
      const current = query.state.data;
      if (!current || SETTLED.has(current.status)) return false;
      if (importIsArriving(current) && until?.(current)) return false;
      return IMPORT_POLL_MS;
    },
  });

  // Compared by value, per import. The first reading of an import is the
  // baseline, not a change: whatever started it has already refreshed the
  // lists.
  const counts = query.data ? countsOf(query.data) : null;
  const status = query.data?.status ?? null;
  const seen = useRef<{ importId: string; counts: string; status: ImportStatus } | null>(
    null,
  );
  useEffect(() => {
    if (!projectId || !importId || counts === null || status === null) return;
    const before = seen.current;
    seen.current = { importId, counts, status };
    if (before?.importId !== importId) return;
    const settled = SETTLED.has(status) && !SETTLED.has(before.status);
    if (before.counts === counts && !settled) return;
    invalidatePlacement(queryClient, projectId);
  }, [projectId, importId, counts, status, queryClient]);

  return query;
}

/**
 * Every import of the project, polled while the server is working on any of
 * them.
 *
 * This is how a screen learns that an import has been organised when nothing
 * on it is following that import: setup's review step and the Brain. An
 * `open` import is not polled, because only its client can move it on.
 * Closing one re-reads this list (`closeImport` below), which is what starts
 * the poll.
 *
 * When any import moves on, the Brain is re-read, because a file's category
 * comes with its card (`document-ai/0032`, 2026-10-03): an import that has
 * moved past reading has carded its files, and the shelves would otherwise
 * say "Not sorted yet" until it settles. When one settles, the documents are
 * re-read as well.
 */
export function useImports(projectId: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: importKeys.list(projectId ?? ""),
    enabled: Boolean(projectId),
    queryFn: async (): Promise<ProjectImport[]> => {
      const res = await api.projects.listImports({ params: { id: projectId! } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.imports;
    },
    refetchInterval: (query) =>
      (query.state.data ?? []).some((current) => IN_HAND.has(current.status))
        ? IMPORT_POLL_MS
        : false,
  });

  // Each import's status as last seen, per project. The first reading is the
  // baseline. After it, an import seen for the first time has moved: it was
  // opened or closed while the screen was up, and a small one can already be
  // organised by the time the list is re-read.
  const { data: imports } = query;
  const seen = useRef<{ projectId: string; statuses: Map<string, ImportStatus> } | null>(
    null,
  );
  useEffect(() => {
    if (!projectId || !imports) return;
    const before = seen.current?.projectId === projectId ? seen.current.statuses : null;
    const now = new Map(imports.map((current) => [current.id, current.status]));
    seen.current = { projectId, statuses: now };
    if (!before) return;

    let moved = false;
    let settled = false;
    for (const [id, status] of now) {
      const was = before.get(id);
      if (was === status) continue;
      moved = true;
      if (SETTLED.has(status) && (was === undefined || !SETTLED.has(was))) settled = true;
    }
    if (settled) invalidatePlacement(queryClient, projectId);
    else if (moved) void queryClient.invalidateQueries({ queryKey: ["project", projectId, "brain"] });
  }, [projectId, imports, queryClient]);

  return query;
}

function countsOf(current: ProjectImport): string {
  return [
    current.files,
    current.unpacked,
    current.carded,
    current.read,
    current.failed,
    current.refused,
    current.failures.length,
  ].join("/");
}

/**
 * Open an import for browser uploads.
 *
 * A failure here is not the upload's failure. The files still go up, each on
 * its own, and get a card but no bundle. Refusing a file because its bundle
 * could not be opened would rank the grouping above the thing being grouped.
 */
async function openImport(
  projectId: string | undefined,
  { programme = false }: { programme?: boolean } = {},
): Promise<string | null> {
  if (!projectId) return null;
  try {
    const res = await api.projects.openImport({
      params: { id: projectId },
      body: { source: "local", ...(programme ? { programme } : {}) },
    });
    if (res.status === 201) return res.body.import.id;
    console.warn(
      "Couldn’t open an import; these files arrive on their own.",
      res.status,
    );
  } catch (error) {
    console.warn(
      "Couldn’t open an import; these files arrive on their own.",
      error,
    );
  }
  return null;
}

/** How many times a close is tried. It is idempotent, so trying again is safe. */
const CLOSE_ATTEMPTS = 3;

/**
 * Say that is every file (`document-ai/0019` §2).
 *
 * Retried because an import left open is never organised, and nobody on the
 * screen would see that it was not. The contract makes the close idempotent
 * for exactly this.
 */
async function closeImport(projectId: string, importId: string): Promise<void> {
  for (let attempt = 1; ; attempt += 1) {
    let status = 0;
    try {
      const res = await api.projects.closeImport({
        params: { id: projectId, importId },
        body: {},
      });
      status = res.status;
    } catch {
      // A network failure: worth another try.
    }
    if (status === 200) return;
    // A 404 is an import that is not there, and will not be there next time.
    if (status === 404 || attempt >= CLOSE_ATTEMPTS) {
      console.warn(`Couldn’t close import ${importId}; it stays open.`, status);
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, attempt * 1_000));
  }
}

/**
 * Delete the server's row for an upload a person dismissed.
 *
 * Best effort. A 404 is a row already gone — a completion refused for its
 * size deletes its own — and anything else leaves the row to be removed from
 * the import's review, where it is listed.
 */
async function discardUnfinished(projectId: string, documentId: string): Promise<void> {
  try {
    const res = await api.projects.deleteDocument({ params: { id: projectId, docId: documentId } });
    if (res.status !== 204 && res.status !== 404) {
      console.warn(`Couldn’t remove the unfinished upload ${documentId}.`, res.status);
    }
  } catch (error) {
    console.warn(`Couldn’t remove the unfinished upload ${documentId}.`, error);
  }
}

// ------------------------------------------------------------------
// Uploads
// ------------------------------------------------------------------

/**
 * One import, as the queue holds it.
 *
 * In refs rather than state, like `inFlight`: rows join and settle inside
 * `run`, and whether to close has to be decided on the count as it is now, not
 * as the last render saw it.
 */
type Bundle = {
  /** Captured when opened, so a close always goes to the project it was opened in. */
  projectId: string | undefined;
  /** The server's id, or null when it could not be opened. Awaited before a presign. */
  id: Promise<string | null>;
  /** Rows in it that have not settled yet. */
  pending: Set<string>;
  /** No new row may join it: set when its drop has been added. */
  sealed: boolean;
  /** The close has been sent, or there was nothing to close. */
  closed: boolean;
};

/** The files sent since nothing was last going, and how many have settled. */
export type UploadBatch = { total: number; settled: number };

export type UploadState = {
  /** Client-side id — the server's document id only exists after presigning. */
  key: string;
  /** The server's id, once presigned. What the row is handed over on. */
  documentId?: string;
  /** The import it was presigned into. Absent for a file that arrived on its own. */
  importId?: string;
  filename: string;
  /**
   * The folder it was dropped or picked in, sent with the presign
   * (2026-09-26, LIN-01). Null for a file picked on its own.
   */
  sourcePath: string | null;
  sizeBytes: number;
  status: "queued" | "uploading" | "processing" | "done" | "error";
  /** 0–100. Meaningful while `uploading`. */
  progress: number;
  error?: string;
  /**
   * The file was refused, and would be refused again: it is empty, too large,
   * of a type we do not store. The row offers Dismiss and no Retry.
   */
  refused?: boolean;
  /** The original file, kept so a failed upload can be retried. */
  file: File;
};

/**
 * When a file was last changed on the person's disk, as an ISO time: the
 * browser's `File.lastModified` (LIN-25). Null when the browser gives none.
 * The API checks it again (`normaliseFileTime`).
 */
function lastChanged(file: File): string | null {
  const at = file.lastModified;
  return typeof at === "number" && Number.isFinite(at) && at > 0 ? new Date(at).toISOString() : null;
}

/** Concurrent uploads in flight. The rest queue. */
const MAX_CONCURRENT = 4;

let counter = 0;
function nextKey(): string {
  counter += 1;
  return `upload-${counter}`;
}

/**
 * Manage a queue of uploads for one project.
 *
 * Failures leave a retryable row rather than making the file vanish, which is
 * the difference between "that didn't work, try again" and the user believing
 * a document is on file when it never arrived.
 *
 * Each drop is an import of its own (`document-ai/0019` §2): a file added
 * later is a new bundle, not stirred into one that may already be under
 * review. The import is opened before the first presign that needs it, and
 * closed once the files in it have settled — done or failed. Closing any
 * sooner would have the server refuse a file still on its way: the presign is
 * refused with a 404 once an import is closed.
 *
 * Setup's Documents step kept one import open across every drop on it, until
 * 2026-10-03, when the step went (`product/0016`).
 *
 * An uploaded file is checked against the project once its bundle is organised
 * (`document-ai/0013`). Nothing is said here while it goes up: the check has
 * not run yet, and a file that looks like another project's is shown where a
 * person can act on it — the Brain's shelf that asks, and the file's own
 * viewer.
 */
export function useUploads(projectId: string | undefined) {
  const queryClient = useQueryClient();
  const [uploads, setUploads] = useState<UploadState[]>([]);
  // The files sent since the last time nothing was going, and how many of
  // them have settled (2026-10-03, the owner): "Uploading 3 of 7 documents".
  // Rows alone cannot say it, because a filed row is handed over and gone.
  // A refused file is never sent, so it is not counted.
  const [batch, setBatch] = useState<UploadBatch>({ total: 0, settled: 0 });
  // The rows as last committed. A dismiss reads the row it removes from here
  // rather than inside a state updater, which may run twice.
  const rowsRef = useRef(uploads);
  rowsRef.current = uploads;

  // The same query the list renders — an extra observer, not an extra fetch.
  // A finished upload has to hand its row over to the document it became, and
  // the only honest signal for that is the document being in the list.
  const { data: documents } = useDocuments(projectId);
  const filed = useMemo(
    () => new Set((documents ?? []).map((document) => document.id)),
    [documents],
  );
  // Held in refs as well as state: the pump decides what to start
  // synchronously, and React's state updates are batched.
  const inFlight = useRef(0);
  // Keys already handed to `run`. Without this, two `pump` calls landing before
  // the first `patch` commits both see the same row as `queued` and upload the
  // same file twice — which is exactly what dropping four files at once does.
  const started = useRef(new Set<string>());
  // Which import each row belongs to, and every import not yet closed. A row
  // keeps its entry after it settles, so a retry can tell whether its import is
  // still open.
  const bundleOf = useRef(new Map<string, Bundle>());
  const bundles = useRef(new Set<Bundle>());

  const patch = useCallback((key: string, next: Partial<UploadState>) => {
    setUploads((current) =>
      current.map((u) => (u.key === key ? { ...u, ...next } : u)),
    );
  }, []);

  const openBundle = useCallback((): Bundle => {
    const bundle: Bundle = {
      projectId,
      id: Promise.resolve(null),
      pending: new Set(),
      sealed: false,
      closed: false,
    };
    bundle.id = openImport(projectId);
    bundles.current.add(bundle);
    return bundle;
  }, [projectId]);

  /** Close an import once it is sealed and nothing in it is still going. */
  const closeIfSettled = useCallback((bundle: Bundle) => {
    if (bundle.closed || !bundle.sealed || bundle.pending.size > 0) return;
    // Marked before the request, so a retry from here on uploads on its own
    // rather than into an import that is being closed.
    bundle.closed = true;
    bundles.current.delete(bundle);
    const { projectId: owner } = bundle;
    void bundle.id.then(async (id) => {
      if (!id || !owner) return;
      await closeImport(owner, id);
      // Closed, the import is the server's to move on, and nothing polls an
      // open one. Re-reading the list is what starts `useImports` following it
      // to its end — on whichever screen is up by then.
      void queryClient.invalidateQueries({ queryKey: importKeys.list(owner) });
    });
  }, [queryClient]);

  /** A row has settled — done, failed or dismissed — and its import may close. */
  const settle = useCallback(
    (key: string) => {
      const bundle = bundleOf.current.get(key);
      if (!bundle) return;
      bundle.pending.delete(key);
      closeIfSettled(bundle);
    },
    [closeIfSettled],
  );

  // `run` starts the next queued upload when it finishes, and `pump` starts
  // `run` — a genuine cycle. The indirection through a ref breaks it without
  // either closure going stale, which a plain mutual reference would.
  const pumpRef = useRef<() => void>(() => {});

  const run = useCallback(
    async (upload: UploadState): Promise<void> => {
      if (!projectId) return;
      inFlight.current += 1;
      patch(upload.key, { status: "uploading", progress: 0, error: undefined, refused: false });
      let documentId = upload.documentId;

      const done = (): void => {
        patch(upload.key, { status: "done" });
        // The row now stands in for a document that exists. It is cleared by
        // the handover below, when this invalidation brings that document
        // back — not on a timer racing the same refetch.
        void queryClient.invalidateQueries({
          queryKey: documentKeys.list(projectId),
        });
        // The Brain lists the same documents under its own key — see the note in
        // `connectors-api.ts`.
        void queryClient.invalidateQueries({
          queryKey: ["project", projectId, "brain"],
        });
      };

      try {
        let target: { documentId: string; uploadUrl: string } | null = null;

        // **A retry keeps its row** (`document-ai/0019` §8, 2026-09-25). A
        // retry used to presign a new row, and the old one stayed behind,
        // pending for good and counted as a file. Its bytes may even have
        // arrived: a lost `complete` looks the same from here as a lost PUT.
        // So `complete` is asked first; then the same row is presigned again;
        // and only a row the server no longer has is made anew.
        if (documentId) {
          const complete = await api.projects.completeUpload({
            params: { id: projectId, docId: documentId },
            body: {},
          });
          if (complete.status === 200) return done();
          if (complete.status === 413) throw new UploadRefused(errorMessage(complete));
          if (complete.status === 409) {
            const reopened = await api.projects.reopenUpload({
              params: { id: projectId, docId: documentId },
              body: {},
            });
            // Stored in between, by an earlier request that finally landed.
            if (reopened.status === 409) return done();
            if (reopened.status === 201) target = reopened.body;
            else if (reopened.status !== 404) throw new Error(errorMessage(reopened));
          } else if (complete.status !== 404) {
            throw new Error(errorMessage(complete));
          }
        }

        if (!target) {
          // The import first, because the presign is what puts the file in it.
          // Awaited inside the cap: a row waiting for its import counts as in
          // flight, so the wait cannot let more than `MAX_CONCURRENT` start.
          const bundle = bundleOf.current.get(upload.key);
          const importId = (bundle ? await bundle.id : null) ?? undefined;
          const presign = await api.projects.uploadUrl({
            params: { id: projectId },
            body: {
              filename: upload.file.name,
              mimeType: upload.file.type || "application/octet-stream",
              sizeBytes: upload.file.size,
              ...(importId ? { importId } : {}),
              ...(upload.sourcePath ? { sourcePath: upload.sourcePath } : {}),
              // When the file was last changed on the person's disk (LIN-25,
              // 2026-09-26): a hint, never a reason to refuse it.
              ...(lastChanged(upload.file) ? { modifiedAt: lastChanged(upload.file)! } : {}),
            },
          });
          if (isRefusal(presign.status)) throw new UploadRefused(errorMessage(presign));
          if (presign.status !== 201) throw new Error(errorMessage(presign));
          target = presign.body;
          documentId = presign.body.documentId;
          patch(upload.key, { documentId, importId });
        }

        await putWithProgress(target.uploadUrl, upload.file, (percent) =>
          patch(upload.key, { progress: percent }),
        );

        // `complete` verifies the object and queues the parse rather than
        // running it, so this covers the verification alone. Extraction and
        // the card carry on afterwards, and the document row in the list
        // reports them through `processingStatus`.
        patch(upload.key, { status: "processing", progress: 100 });

        const complete = await api.projects.completeUpload({
          params: { id: projectId, docId: target.documentId },
          body: {},
        });
        if (complete.status === 413) throw new UploadRefused(errorMessage(complete));
        if (complete.status !== 200) throw new Error(errorMessage(complete));

        done();
      } catch (error) {
        patch(upload.key, { status: "error", ...uploadFailure(error) });
        // A `complete` whose reply was lost may still have stored the file.
        // Re-reading the list lets the handover below find it and clear the
        // row, rather than leave an error standing over a filed document.
        if (documentId) {
          void queryClient.invalidateQueries({ queryKey: documentKeys.list(projectId) });
        }
      } finally {
        inFlight.current -= 1;
        setBatch((before) => ({ ...before, settled: before.settled + 1 }));
        settle(upload.key);
        pumpRef.current();
      }
    },
    [projectId, patch, queryClient, settle],
  );

  /**
   * Start as many queued uploads as the concurrency cap allows.
   *
   * The queue is read inside `setUploads` rather than from a captured value, so
   * it sees the state React has actually committed — four files dropped at once
   * arrive in one batch, and a stale read would start the same one twice.
   */
  const pump = useCallback(() => {
    setUploads((current) => {
      const starting = current
        .filter((u) => u.status === "queued" && !started.current.has(u.key))
        .slice(0, Math.max(0, MAX_CONCURRENT - inFlight.current));
      for (const upload of starting) {
        started.current.add(upload.key);
        void run(upload);
      }
      return current;
    });
  }, [run]);

  pumpRef.current = pump;

  /**
   * Files to upload. A bare `File` carries its folder in `webkitRelativePath`
   * when a folder picker gave it; a file picked on its own has none.
   */
  const add = useCallback(
    (added: readonly (File | PickedFile)[]) => {
      const files = withFolders(added);
      if (files.length === 0) return;
      // Built out here, not inside the updater: the rows join their import
      // below, and an updater may run twice.
      const rows: UploadState[] = files.map(({ file, sourcePath }) => {
        // Checked before any request (`architecture/0031`). A refused file is
        // a row at once, with the sentence, and never goes into an import.
        const refused = documentRefusal(file);
        return {
          key: nextKey(),
          filename: file.name,
          sourcePath,
          sizeBytes: file.size,
          ...(refused
            ? { status: "error" as const, error: refused, refused: true }
            : { status: "queued" as const }),
          progress: 0,
          file,
        };
      });
      const going = rows.filter((row) => !row.refused);
      // Joined before the rows exist, so no row can start without its import.
      // A drop in which every file was refused opens none.
      if (going.length > 0) {
        const bundle = openBundle();
        for (const row of going) {
          bundle.pending.add(row.key);
          bundleOf.current.set(row.key, bundle);
        }
        // A drop is its own bundle: nothing else joins it.
        bundle.sealed = true;
      }
      if (going.length > 0) {
        setBatch((before) =>
          before.settled >= before.total
            ? { total: going.length, settled: 0 }
            : { ...before, total: before.total + going.length },
        );
      }
      setUploads((current) => [...current, ...rows]);
      // After the state commit, so the queued rows exist for `pump` to find.
      setTimeout(pump, 0);
    },
    [openBundle, pump],
  );

  const retry = useCallback(
    (key: string) => {
      // Clear the start guard, or the row goes back to `queued` and is never
      // picked up again.
      started.current.delete(key);
      // Back into its import if that is still open. Once it has closed, the
      // server would refuse the presign with a 404, so the file arrives on its
      // own instead — a card, but no bundle.
      const bundle = bundleOf.current.get(key);
      if (bundle?.closed) bundleOf.current.delete(key);
      else bundle?.pending.add(key);
      patch(key, {
        status: "queued",
        error: undefined,
        refused: false,
        progress: 0,
        importId: undefined,
      });
      // It settled once as a failure; it is going again. A batch already over
      // starts again with this one file.
      setBatch((before) =>
        before.settled >= before.total
          ? { total: 1, settled: 0 }
          : { ...before, settled: Math.max(0, before.settled - 1) },
      );
      setTimeout(pump, 0);
    },
    [patch, pump],
  );

  const dismiss = useCallback(
    (key: string) => {
      const row = rowsRef.current.find((u) => u.key === key);
      started.current.delete(key);
      setUploads((current) => current.filter((u) => u.key !== key));
      // A dismissed row never settles by itself, and its import must not wait
      // for it.
      settle(key);
      bundleOf.current.delete(key);
      // Its row on the server goes too (2026-09-25). Left, it would be listed
      // on its import's review as an upload that did not finish, for a file
      // the person has already said they do not want.
      if (projectId && row?.documentId && row.status === "error") {
        void discardUnfinished(projectId, row.documentId).then(() =>
          queryClient.invalidateQueries({ queryKey: importKeys.list(projectId) }),
        );
      }
    },
    [settle, projectId, queryClient],
  );

  // Leaving the screen is the end of every import it opened. A row that has
  // not started never will — its pump goes with the screen — so it is not
  // waited for. A row already going up carries on, and closes its import when
  // it settles. Without this, a step left by Back, or a tab closed mid-drop,
  // would leave an import open that is never organised.
  useEffect(() => {
    const open = bundles.current;
    const begun = started.current;
    return () => {
      for (const bundle of open) {
        bundle.sealed = true;
        for (const key of bundle.pending) {
          if (!begun.has(key)) bundle.pending.delete(key);
        }
        closeIfSettled(bundle);
      }
    };
  }, [closeIfSettled]);

  // A row whose document is in the list has nothing left to stand in for.
  // Held back from the return below rather than deleted here, so the two never
  // overlap in a painted frame; this effect only frees the state afterwards.
  // Status is not consulted on purpose — a `complete` whose response was lost
  // leaves an errored row for a document that is safely filed, and that row
  // should go too.
  useEffect(() => {
    const handed = uploads.filter(
      (u) => u.documentId && filed.has(u.documentId),
    );
    if (handed.length === 0) return;
    const gone = new Set(handed.map((u) => u.key));
    setUploads((current) => current.filter((u) => !gone.has(u.key)));
  }, [uploads, filed]);

  return {
    uploads: uploads.filter((u) => !(u.documentId && filed.has(u.documentId))),
    batch,
    add,
    retry,
    dismiss,
  };
}
